import { Box, Typography, IconButton, Container } from "@mui/material";
import { useState } from "react";
import PropTypes from "prop-types";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import StarIcon from "@mui/icons-material/Star";

const TestimonialsCarouselSection = ({
  testimonials = [],
  title,
  subtitle,
  description,
}) => {
  // Default testimonial data
  const defaultTestimonials = [
    {
      id: 1,
      name: "Dr. Neha Rani",
      title: "Clinical Nutritionist",
      rating: 5,
      quote:
        "Scheduling, payments, client updates   all in one place. Fitify gave me back my time to focus on what truly matters: my clients. I no longer spend hours juggling apps and spreadsheets. Everything I need is beautifully organized, automated, and right at my fingertips.",
      image: require("assets/images/testimonial.png"),
      alt: "Dr. Neha Rani - Clinical Nutritionist",
    },
    {
      id: 2,
      name: "Sarah M",
      title: "Registered Dietitian",
      rating: 5,
      quote:
        "Fitify has revolutionized how I manage my practice. The automation features save me hours every week, allowing me to focus more on patient care and less on administrative tasks.",
      image: require("assets/images/testimonial.png"),
      alt: "Sarah M - Registered Dietitian",
    },
    {
      id: 3,
      name: "Dr. Michael Chen",
      title: "Sports Nutritionist",
      rating: 5,
      quote:
        "The client management system is intuitive and powerful. My clients love the seamless experience, and I love how everything is organized in one place.",
      image: require("assets/images/testimonial.png"),
      alt: "Dr. Michael Chen - Sports Nutritionist",
    },
    {
      id: 4,
      name: "Emily Rodriguez",
      title: "Clinical Dietitian",
      rating: 5,
      quote:
        "From scheduling to payments, Fitify handles everything beautifully. It's like having a personal assistant that never sleeps. Highly recommend to any nutrition professional.",
      image: require("assets/images/testimonial.png"),
      alt: "Emily Rodriguez - Clinical Dietitian",
    },
  ];

  const testimonialsData =
    testimonials.length > 0 ? testimonials : defaultTestimonials;
  const [currentIndex, setCurrentIndex] = useState(0);

  const handlePrevious = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? testimonialsData.length - 1 : prevIndex - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === testimonialsData.length - 1 ? 0 : prevIndex + 1
    );
  };

  const handleDotClick = (index) => {
    setCurrentIndex(index);
  };

  const currentTestimonial = testimonialsData[currentIndex];

  return (
    <Box sx={{ py: { xs: 6, md: 8 } }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
          <Typography
            variant="h6"
            sx={{
              color: "#17c1e8",
              fontSize: { xs: "1rem", md: "1.1rem" },
              fontWeight: 500,
              mb: 2,
              fontFamily: "Outfit",
            }}
          >
            {subtitle || "Testimonials"}
          </Typography>

          <Typography
            variant="h2"
            sx={{
              color: "#FFFFFF",
              fontSize: {
                xs: "1.8rem",
                sm: "2.2rem",
                md: "2.8rem",
                lg: "3.2rem",
              },
              fontWeight: 600,
              mb: 2,
              lineHeight: 1.2,
              fontFamily: "Outfit",
            }}
          >
            {title || "Check what our clients are saying"}
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: "rgba(255, 255, 255, 0.8)",
              fontWeight: 400,
              margin: "0 auto",
              lineHeight: 1.6,
              fontSize: { xs: "0.95rem", md: "1.1rem" },
              fontFamily: "Outfit",
              maxWidth: "600px",
            }}
          >
            {description ||
              "Streamline client management, automate payments, and scale effortlessly all from one dashboard."}
          </Typography>
        </Box>

        {/* Testimonial Carousel */}
        <Box
          sx={{
            maxWidth: "1000px",
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Testimonial Card */}
          <Box
            sx={{
              backgroundColor: "rgba(45, 45, 45, 0.8)",
              borderRadius: "20px",
              padding: { xs: "24px 48px", md: "40px 120px" },
              width: "100%",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)",
              display: "flex",
              alignItems: "flex-start",
              gap: 3,
              position: "relative",
            }}
          >
            {/* Left Navigation Arrow - Inside the card */}
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                left: "10px",
                zIndex: 10,
              }}
            >
              <IconButton
                onClick={handlePrevious}
                sx={{
                  backgroundColor: "#17c1e8",
                  color: "white",
                  width: { xs: "40px", md: "50px" },
                  height: { xs: "40px", md: "50px" },
                  "&:hover": {
                    backgroundColor: "#00bcd4",
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <ChevronLeftIcon
                  sx={{ fontSize: { xs: "1.5rem", md: "2rem" } }}
                />
              </IconButton>
            </Box>

            {/* Right Navigation Arrow - Inside the card */}
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                right: "10px",
                zIndex: 10,
              }}
            >
              <IconButton
                onClick={handleNext}
                sx={{
                  backgroundColor: "#17c1e8",
                  color: "white",
                  width: { xs: "40px", md: "50px" },
                  height: { xs: "40px", md: "50px" },
                  "&:hover": {
                    backgroundColor: "#00bcd4",
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <ChevronRightIcon
                  sx={{ fontSize: { xs: "1.5rem", md: "2rem" } }}
                />
              </IconButton>
            </Box>
            {/* Profile Image */}
            <Box
              sx={{
                width: { xs: "100px", md: "250px" },
                height: { xs: "100px", md: "400px" },
                borderRadius: "10px",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              <Box
                component="img"
                src={currentTestimonial.image}
                alt={currentTestimonial.alt}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </Box>

            {/* Content Area */}
            <Box sx={{ flex: 1 }}>
              {/* Star Rating */}
              <Box sx={{ display: "flex", gap: 0.5, mb: 2 }}>
                {[...Array(currentTestimonial.rating)].map((_, index) => (
                  <StarIcon
                    key={index}
                    sx={{
                      color: "#FFD700",
                      fontSize: "1.2rem",
                    }}
                  />
                ))}
              </Box>

              {/* Quote */}
              <Typography
                sx={{
                  color: "#FFFFFF",
                  fontSize: { xs: "0.95rem", md: "1.1rem" },
                  lineHeight: 1.6,
                  fontFamily: "Outfit",
                  mb: 8,
                }}
              >
                "{currentTestimonial.quote}"
              </Typography>

              {/* Client Info */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    color: "#17c1e8",
                    fontSize: { xs: "1rem", md: "1.1rem" },
                    fontWeight: "bold",
                    fontFamily: "Outfit",
                    mb: 0.5,
                  }}
                >
                  {currentTestimonial.name}
                </Typography>
                <Typography
                  sx={{
                    color: "#FFFFFF",
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    fontFamily: "Outfit",
                    opacity: 0.9,
                  }}
                >
                  {currentTestimonial.title}
                </Typography>
              </Box>

              {/* Pagination Dots - Inside the card at bottom */}
              <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
                {testimonialsData.map((_, index) => (
                  <Box
                    key={index}
                    onClick={() => handleDotClick(index)}
                    sx={{
                      width: "12px",
                      height: "12px",
                      borderRadius: "50%",
                      backgroundColor:
                        index === currentIndex
                          ? "#FFFFFF"
                          : "rgba(255, 255, 255, 0.3)",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor:
                          index === currentIndex
                            ? "#FFFFFF"
                            : "rgba(255, 255, 255, 0.6)",
                        transform: "scale(1.2)",
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

TestimonialsCarouselSection.propTypes = {
  testimonials: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      rating: PropTypes.number.isRequired,
      quote: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
    })
  ),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  description: PropTypes.string,
};

export default TestimonialsCarouselSection;
